// cSpell:ignore aandeel eigenaar eigenaarsdeel gunste laste
// An owner's part of one cost by its cause (Lewis, 2026-10-19): the fixed
// owner share, which is never the tenant's, and the vacancy, the tenant's
// part of the days the unit had no tenant. The owner's card shows each on
// a row of its own with its amount incl. VAT; the cost's split in the cost
// drawer lists them apart too.
//
// The engine keeps one VAT figure per owner and cost (OwnerCostLine.vat),
// none per cause, so it is split here: the departure differences carry
// their own VAT, and the rest goes over the fixed share and the vacancy by
// their amounts, in whole cents, the fixed share's rounded and the
// vacancy's the remainder. The two causes add up to the line's own part to
// the cent.

import {
  IDepartureCorrection,
  isDepartureCorrectionKey,
  parseDepartureCorrectionKey,
} from "./departureCorrection.utils";
import { daysInPeriod, OwnerCostLine } from "./engine";
import { fromCents, toCents } from "./money.utils";

// Less than half a cent is no part.
const HALF_CENT = 0.005;

type TOwnerLineFigures = Pick<
  OwnerCostLine,
  "amount" | "base" | "vacancy" | "vat" | "departureDiffs"
>;

// An amount incl. VAT, and the VAT in it.
export interface IOwnerCausePart {
  amount: number;
  vat: number;
}

const sumCents = (amounts: number[]): number =>
  amounts.reduce((total, amount) => total + toCents(amount), 0);

// The VAT of the line's fixed share and of its vacancy.
export const ownerLineVatByCause = (
  line: TOwnerLineFigures,
): { base: number; vacancy: number } => {
  const diffs = line.departureDiffs ?? [];
  const restCents = toCents(line.vat) - sumCents(diffs.map(diff => diff.vat));
  const baseCents = toCents(line.base);
  const partsCents = baseCents + toCents(line.vacancy);
  const baseVatCents =
    partsCents === 0
      ? restCents
      : Math.round((restCents * baseCents) / partsCents);
  return {
    base: fromCents(baseVatCents),
    vacancy: fromCents(restCents - baseVatCents),
  };
};

// The line's own part incl. VAT (without its departure differences), and
// that part per cause: absent for a cause the line does not have.
export const ownerLineCauses = (
  line: TOwnerLineFigures,
): {
  total: IOwnerCausePart;
  base?: IOwnerCausePart;
  vacancy?: IOwnerCausePart;
} => {
  const diffs = line.departureDiffs ?? [];
  const vatCents = toCents(line.vat) - sumCents(diffs.map(diff => diff.vat));
  const totalCents =
    toCents(line.amount) - sumCents(diffs.map(diff => diff.amount)) + vatCents;
  const total = { amount: fromCents(totalCents), vat: fromCents(vatCents) };
  const hasBase = Math.abs(line.base) >= HALF_CENT;
  const hasVacancy = Math.abs(line.vacancy) >= HALF_CENT;
  if (!hasBase || !hasVacancy) {
    return {
      total,
      ...(hasBase ? { base: total } : {}),
      ...(hasVacancy ? { vacancy: total } : {}),
    };
  }
  const vat = ownerLineVatByCause(line);
  const vacancyCents = toCents(line.vacancy) + toCents(vat.vacancy);
  return {
    total,
    base: {
      amount: fromCents(totalCents - vacancyCents),
      vat: fromCents(vatCents - toCents(vat.vacancy)),
    },
    vacancy: { amount: fromCents(vacancyCents), vat: vat.vacancy },
  };
};

// What the label of an owner's own part of a cost says (M29, retest of
// 2026-10-20): "Vast eigenaarsdeel" only for a fixed owner share. A
// correction of a lease end that moved, and the part of a reading's cost
// no reading accounts for (the excess), are labelled neutrally: "Ten laste
// van de eigenaar", or for a negative part "Ten gunste van de eigenaar".
export type TOwnerBaseCause = "fixed" | "charged" | "credited";

export const ownerBaseCauseOf = (params: {
  // The line's breakdown key.
  key: string;
  // The part incl. VAT.
  amount: number;
  // The cost's fixed owner %, when it has one.
  ownerPct?: number;
  // The category is split by readings or a metering company's amounts.
  isSplitByReadings: boolean;
}): TOwnerBaseCause => {
  const { key, amount, ownerPct, isSplitByReadings } = params;
  const isNeutral =
    isDepartureCorrectionKey(key) || (isSplitByReadings && ownerPct == null);
  if (!isNeutral) return "fixed";
  return amount < -HALF_CENT ? "credited" : "charged";
};

// What an owner's correction row stands for (its "i"): the tenant whose
// lease end moved, whether they rented longer or shorter, and the days.
// Undefined for another row, or without the settlement's corrections.
export const ownerCorrectionOf = (params: {
  key: string;
  corrections?: IDepartureCorrection[];
}):
  | {
      tenantName: string;
      isLonger: boolean;
      from: string;
      to: string;
      days: number;
    }
  | undefined => {
  const { key, corrections } = params;
  const parsed = parseDepartureCorrectionKey(key);
  if (!parsed) return undefined;
  const correction = (corrections ?? []).find(
    candidate =>
      candidate.settlementId === parsed.settlementId &&
      candidate.from === parsed.from &&
      candidate.to === parsed.to,
  );
  if (!correction) return undefined;
  return {
    tenantName: correction.tenantName,
    isLonger: correction.end > correction.until,
    from: parsed.from,
    to: parsed.to,
    days: daysInPeriod(parsed.from, parsed.to),
  };
};

// The owner's own parts (not the vacancy) as the "i" of the owner card's
// total lists them (M29): the fixed owner shares together, and each
// correction of a lease end and each reading's part of the excess on a
// line of its own. The engine books those two as the owner's base, which
// summed them under "Vast aandeel". The parts add up to the rows to the
// cent; `fixed` is undefined when no row is a fixed share.
export const ownerBaseParts = (params: {
  // The owner's base rows per breakdown key, incl. VAT.
  rows: { key: string; amount: number }[];
  // The key's category is split by readings or a metering company's amounts.
  isSplitByReadings: (key: string) => boolean;
  // The key's fixed owner %, when it has one.
  ownerPctOf: (key: string) => number | undefined;
}): {
  fixed?: number;
  corrections: { key: string; amount: number }[];
  excess: { key: string; amount: number }[];
} => {
  const corrections: { key: string; amount: number }[] = [];
  const excess: { key: string; amount: number }[] = [];
  let fixedCents: number | undefined;
  for (const row of params.rows) {
    if (isDepartureCorrectionKey(row.key)) {
      corrections.push(row);
    } else if (
      params.isSplitByReadings(row.key) &&
      params.ownerPctOf(row.key) == null
    ) {
      excess.push(row);
    } else {
      fixedCents = (fixedCents ?? 0) + toCents(row.amount);
    }
  }
  return {
    ...(fixedCents == null ? {} : { fixed: fromCents(fixedCents) }),
    corrections,
    excess,
  };
};
